// Agency names: the legal name and the DBA ("doing business as").
//
// `agencies.name` is the agency's LEGAL name. It goes on every file we make
// (visit PDFs, the CMS-485, invoices, contracts), to HHAeXchange, in the agency
// portal, in the Admin Hub and in everything we send to the agency itself.
//
// `agencies.dba` is the name the agency's PATIENTS and CAREGIVERS know it by.
// It is what they hear and read from us: confirmation, reminder and
// questionnaire texts, Mia's calls (outbound and inbound) and the questionnaire
// page. Prod 2026-11: agency 42 is "Serenity Health & Wellness, LLC" (its HHAX
// office name), but its patients know it as Swift Home Care, and one of them
// called us scammers after a call from "Serenity".
//
// Every patient- or caregiver-facing path reads the name through
// agencyPublicName(); nothing else reads `dba` directly. deriveAgencyDba() is
// the default DBA for a new agency, and the same rule as the backfill in
// migration 20261101*_agency_dba.sql (a test runs both on the same names).
/** The name patients and caregivers hear: the DBA when set, else the legal name. */
export function agencyPublicName(agency) {
    const dba = agency.dba?.trim();
    return dba ? dba : agency.name;
}
// A trailing " - <office>" segment, e.g. "Anchor Health Home Care Services - Albany".
// Spaces on both sides of the dash, so "In-Home" and "Co-Op" are untouched.
const OFFICE_SEGMENT = /\s+[-–]\s+.*$/;
// Trailing legal suffixes, one or more, each with an optional comma before it and
// an optional period after it: "Royal Care, Inc.", "Acme Corp, Inc.". Whole words
// only (so "Incare" keeps its "Inc"), and "Co" is not stripped after "&" (so
// "Smith & Co." stays a name).
const LEGAL_SUFFIX = /(?:\s*,?\s*\b(?:inc|incorporated|llc|l\.l\.c|corp|corporation|ltd|pllc|llp|(?<!&\s{0,3})co)\b\.?)+\s*$/i;
const EDGE_SPACES_AND_COMMAS = /^[\s,]+|[\s,]+$/g;
/** Postgres initcap(): first letter of each run of letters/digits upper, the rest lower. */
function initcap(text) {
    let out = "";
    let inWord = false;
    for (const ch of text) {
        const isAlnum = /[\p{L}\p{N}]/u.test(ch);
        out += isAlnum ? (inWord ? ch.toLowerCase() : ch.toUpperCase()) : ch;
        inWord = isAlnum;
    }
    return out;
}
/**
 * The default DBA for an agency: its legal name without the trailing legal
 * suffix (Inc, LLC, Corp...) or " - <office>" segment, title-cased when the name
 * is ALL CAPS, so it sounds right on a phone call. A name that is nothing but a
 * suffix ("Inc") is kept as it is. Null only for an empty name.
 *
 * Keep in step with the backfill SQL in the agency_dba migration.
 */
export function deriveAgencyDba(legalName) {
    const original = legalName.replace(/^\s+|\s+$/g, "");
    if (!original) {
        return null;
    }
    const withoutOffice = original.replace(OFFICE_SEGMENT, "") || original;
    const stripped = withoutOffice.replace(LEGAL_SUFFIX, "").replace(EDGE_SPACES_AND_COMMAS, "");
    if (!stripped) {
        return original;
    }
    return stripped.toUpperCase() === stripped ? initcap(stripped) : stripped;
}
/**
 * The DBA to store when an agency's legal name changes. A DBA that is still the
 * one derived from the old legal name (nobody typed it) follows the new name; a
 * DBA a person set ("Swift Home Care") stays, and an empty one stays empty (it
 * already falls back to the legal name).
 */
export function dbaAfterLegalRename(previous, newLegalName) {
    if (previous.dba == null) {
        return null;
    }
    return previous.dba === deriveAgencyDba(previous.name)
        ? deriveAgencyDba(newLegalName)
        : previous.dba;
}
/** A DBA as typed by a person: trimmed, empty means "use the legal name" (null). */
export function normalizeAgencyDbaInput(dba) {
    const trimmed = dba?.trim();
    return trimmed ? trimmed : null;
}
//# sourceMappingURL=agency-name.js.map